import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Zap, Plus, Trash2, ChevronDown, ChevronUp, Check, Sparkles, Smartphone, BellOff, Bell, Paperclip, Info, Loader2, X, Image as ImageIcon, Film, Save, Pencil, Package, Clock, Copy } from 'lucide-react';
import axios from './wabotAxios';
import { toast } from 'sonner';

import { WABOT_API, WABOT_BACKEND_ORIGIN } from './wabotApi';
const API = WABOT_API;
const BACKEND_ORIGIN = WABOT_BACKEND_ORIGIN;

// Auto-save debounce hook
function useDebouncedSave(saveFn, delay = 700) {
  const timer = useRef(null);
  const [savingState, setSavingState] = useState('idle'); // idle | saving | saved
  return useCallback((...args) => {
    setSavingState('saving');
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        await saveFn(...args);
        setSavingState('saved');
        setTimeout(() => setSavingState('idle'), 1500);
      } catch (e) {
        setSavingState('idle');
        toast.error('שגיאה בשמירה');
      }
    }, delay);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

const WaBotTriggers = () => {
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [triggers, setTriggers] = useState([]);
  const [aiExpanded, setAiExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null); // id of trigger currently being autosaved
  const [globalSaved, setGlobalSaved] = useState(false);

  const fetchSessions = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/wabot/sessions`);
      const list = r.data.sessions || [];
      setSessions(list);
      // Pick first connected as default
      if (!activeSession && list.length > 0) {
        const connected = list.find(s => s.status === 'connected') || list[0];
        setActiveSession(connected.id);
      }
    } catch (e) {}
  }, [activeSession]);

  const fetchTriggers = useCallback(async () => {
    if (!activeSession) { setTriggers([]); setLoading(false); return; }
    try {
      const r = await axios.get(`${API}/wabot/triggers`);
      const filtered = (r.data.triggers || []).filter(t => t.session_id === activeSession);
      setTriggers(filtered.reverse()); // oldest first - so numbering is stable
    } catch (e) {}
    setLoading(false);
  }, [activeSession]);

  useEffect(() => { fetchSessions(); const t = setInterval(fetchSessions, 5000); return () => clearInterval(t); }, [fetchSessions]);
  useEffect(() => { fetchTriggers(); }, [fetchTriggers]);

  const currentSession = sessions.find(s => s.id === activeSession);
  const isSilenced = currentSession?.silenced;

  const toggleSilence = async () => {
    try {
      await axios.post(`${API}/wabot/sessions/${activeSession}/silence`, { silenced: !isSilenced });
      toast.success(isSilenced ? 'הבוט הופעל' : 'הבוט הושתק');
      fetchSessions();
    } catch (e) { toast.error('שגיאה'); }
  };

  const addTrigger = async () => {
    if (!activeSession) { toast.error('בחר מכשיר תחילה'); return; }
    try {
      const r = await axios.post(`${API}/wabot/triggers`, {
        session_id: activeSession, keyword: '', response: '', match_type: 'contains', enabled: true,
      });
      setTriggers(prev => [...prev, r.data]);
      // scroll new card into view
      setTimeout(() => {
        const el = document.querySelector(`[data-trigger-id="${r.data.id}"]`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    } catch (e) { toast.error('שגיאה'); }
  };

  const updateTriggerLocal = (id, patch) => {
    setTriggers(prev => prev.map(t => t.id === id ? { ...t, ...patch } : t));
  };

  const saveTriggerToServer = async (id, patch) => {
    setSavingId(id);
    try {
      await axios.put(`${API}/wabot/triggers/${id}`, patch);
      setGlobalSaved(true);
      setTimeout(() => setGlobalSaved(false), 1500);
    } finally {
      setSavingId(null);
    }
  };

  // debounced saver per-trigger
  const saveTimers = useRef({});
  const scheduleSave = (id, patch) => {
    if (saveTimers.current[id]) clearTimeout(saveTimers.current[id]);
    saveTimers.current[id] = setTimeout(() => saveTriggerToServer(id, patch), 700);
  };

  const handleField = (id, field, value) => {
    updateTriggerLocal(id, { [field]: value });
    scheduleSave(id, { [field]: value });
  };

  const removeTrigger = async (id) => {
    if (!window.confirm('למחוק את הטריגר?')) return;
    try {
      await axios.delete(`${API}/wabot/triggers/${id}`);
      setTriggers(prev => prev.filter(t => t.id !== id));
    } catch (e) { toast.error('שגיאה'); }
  };

  if (loading && sessions.length === 0) return <div className="p-8 text-center text-stone-400">טוען...</div>;

  return (
    <div className="p-6 max-w-4xl mx-auto" dir="rtl" data-testid="wabot-triggers" style={{ background: 'linear-gradient(180deg, #f3f1ff 0%, #faf9ff 200px, transparent 400px)' }}>
      {/* Page Header */}
      <div className="flex items-start gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-blue-500 flex items-center justify-center flex-shrink-0">
          <Zap className="w-6 h-6 text-white" fill="white" />
        </div>
        <div>
          <h2 className="text-2xl font-black text-[#2C3E2D]">תגובה על טריגר</h2>
          <p className="text-sm text-stone-500 mt-1">תגובות אוטומטיות שיישלחו כשלקוח יכתוב מילה מסוימת – תקף לכל ההודעות הנכנסות למכשיר, תמיד, בלי קשר לקמפיין.</p>
        </div>
      </div>

      {/* No session yet */}
      {!activeSession && (
        <div className="bg-white rounded-2xl p-12 text-center border-2 border-dashed border-stone-200">
          <Smartphone className="w-16 h-16 mx-auto text-stone-300 mb-3" />
          <p className="text-stone-500 font-bold">אין עדיין מכשירים מחוברים</p>
          <p className="text-stone-400 text-sm mt-1">עבור לטאב "המכשירים שלי" והוסף מכשיר תחילה</p>
        </div>
      )}

      {activeSession && (
        <>
          {/* Active Pack Header - shows which trigger pack is currently loaded */}
          <ActivePackHeader sessionId={activeSession} />

          {/* Bot Active Banner */}
          <div className={`relative rounded-2xl p-5 mb-4 border-2 ${isSilenced ? 'bg-stone-50 border-stone-200' : 'bg-green-50 border-green-300'}`} data-testid="wabot-bot-status-banner">
            <div className="flex items-center gap-4">
              <div className={`relative w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 ${isSilenced ? 'bg-stone-300' : 'bg-green-500'}`}>
                <Check className="w-7 h-7 text-white" />
                {!isSilenced && <span className="absolute inset-0 rounded-full bg-green-400 animate-ping opacity-30" />}
              </div>
              <div className="flex-1">
                <h3 className={`font-black text-lg ${isSilenced ? 'text-stone-500' : 'text-green-700'}`}>
                  <span className={`inline-block w-2.5 h-2.5 rounded-full ml-2 ${isSilenced ? 'bg-stone-400' : 'bg-green-500'}`}></span>
                  {isSilenced ? 'הבוט מושתק - לא עונה' : 'הבוט פעיל – עונה ללקוחות אוטומטית'}
                </h3>
                <p className="text-xs text-stone-500 mt-1">
                  פעיל על המכשיר "{currentSession?.name}" · עונה לכל טריגר שמוגדר מטה
                </p>
              </div>
              <button
                onClick={toggleSilence}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-bold text-sm border-2 transition-all ${isSilenced ? 'bg-green-50 text-green-600 border-green-300 hover:bg-green-100' : 'bg-white text-red-500 border-red-300 hover:bg-red-50'}`}
                data-testid="wabot-bot-silence-toggle"
              >
                {isSilenced ? <><Bell className="w-4 h-4" /> הפעל את הבוט</> : <><BellOff className="w-4 h-4" /> השתק את הבוט</>}
              </button>
            </div>
          </div>

          {/* AI Assistant Card - functional Gemini */}
          <AiAssistantCard
            sessionId={activeSession}
            expanded={aiExpanded}
            onToggle={() => setAiExpanded(!aiExpanded)}
          />

          {/* Device Selector */}
          <div className="bg-white rounded-2xl border border-stone-200 p-5 mb-4" data-testid="wabot-device-selector">
            <label className="text-sm font-bold text-stone-700 flex items-center gap-2 mb-2">
              <Smartphone className="w-4 h-4 text-blue-500" />
              המכשיר שהבוט יענה בשמו:
            </label>
            <select
              value={activeSession || ''}
              onChange={e => setActiveSession(e.target.value)}
              className="w-full border-2 border-stone-200 rounded-xl px-4 py-3 text-sm font-bold bg-stone-50 hover:bg-white focus:outline-none focus:border-[#D97706]"
              data-testid="wabot-trigger-device-select"
            >
              {sessions.map(s => (
                <option key={s.id} value={s.id}>
                  {s.name} – {s.status === 'connected' ? 'מחובר' : s.status}
                </option>
              ))}
            </select>
          </div>

          {/* Auto-save indicator */}
          <div className="flex items-center gap-2 text-xs text-stone-500 px-2 mb-3" data-testid="wabot-autosave-indicator">
            {savingId ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> שומר...</>
            ) : globalSaved ? (
              <><Check className="w-3.5 h-3.5 text-green-500" /> נשמר</>
            ) : (
              <><Info className="w-3.5 h-3.5" /> השינויים נשמרים אוטומטית</>
            )}
            <span className="mr-auto bg-stone-100 text-stone-400 px-2 py-0.5 rounded text-[10px] font-mono">Flow #{triggers.length + 1}</span>
          </div>

          {/* Trigger Cards */}
          <div className="space-y-4">
            {triggers.map((t, idx) => (
              <TriggerCard
                key={t.id}
                trigger={t}
                index={idx + 1}
                isSaving={savingId === t.id}
                onChange={(field, value) => handleField(t.id, field, value)}
                onDelete={() => removeTrigger(t.id)}
              />
            ))}
          </div>

          {/* Add Trigger Button */}
          <button
            onClick={addTrigger}
            className="w-full mt-6 bg-white hover:bg-stone-50 border-2 border-dashed border-[#D97706] text-[#D97706] rounded-2xl py-6 font-black text-lg flex items-center justify-center gap-3 transition-all hover:border-solid"
            data-testid="wabot-add-trigger-big-btn"
          >
            <div className="w-10 h-10 rounded-full bg-[#D97706] text-white flex items-center justify-center">
              <Plus className="w-6 h-6" />
            </div>
            הוסף טריגר נוסף
          </button>

          {/* Trigger Packs */}
          <TriggerPacksSection sessionId={activeSession} onApply={fetchTriggers} />

          {/* Help Section */}
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-2xl p-5" data-testid="wabot-help-section">
            <h4 className="font-black text-blue-900 mb-3 flex items-center gap-2">
              <Info className="w-5 h-5" /> איך זה עובד?
            </h4>
            <ul className="text-sm text-blue-800 space-y-1.5 list-disc mr-5">
              <li><b>כמה מילים בטריגר אחד</b> - הפרד בפסיק. למשל "סוכר, סוכרת, יש לי סוכר" → כל אחת מהמילים תפעיל את אותה תגובה.</li>
              <li><b>מכיל</b> – הטריגר יופעל אם המילה מופיעה איפשהו בהודעה (לדוגמה: "מחיר" יענה גם על "כמה מחיר?")</li>
              <li><b>בדיוק</b> – הטריגר יופעל רק אם ההודעה זהה למילה בדיוק (כמו "כן" או "לא")</li>
              <li><b>מתחיל ב-</b> – הטריגר יופעל אם ההודעה מתחילה במילה (לדוגמה: "/הזמנה")</li>
              <li><b>תגובה חוזרת חכמה</b> - אם הוספת "תגובה חוזרת", הבוט ישלח אותה אוטומטית אחרי שהלקוח יענה על הטריגר. השתמש ב-<code className="bg-white px-1 rounded">{'{answer}'}</code> כדי לקבל את התשובה שלו.</li>
              <li><b>AI חכם</b> - אם הפעלת את עוזר ה-AI, הוא יענה <b>אך ורק על בסיס תוכן הטריגרים</b>. אם השאלה לא קשורה לאף טריגר → לא תישלח שום תגובה (במקום להמציא).</li>
              <li>הטריגר הראשון שמתאים יופעל ויעצור (לא נשלחות כמה תגובות לאותה הודעה).</li>
            </ul>
          </div>
        </>
      )}
    </div>
  );
};

// ---------------- Active Pack Header ----------------
// Slim banner shown at the top so the user always sees which group is loaded.
// Re-fetches every 4s + reacts to a custom 'wabot-active-pack-changed' event.
const ActivePackHeader = ({ sessionId }) => {
  const [info, setInfo] = useState(null); // { id, name, count, updated_at }

  const fetch = useCallback(async () => {
    if (!sessionId) { setInfo(null); return; }
    try {
      const r = await axios.get(`${API}/wabot/trigger-packs`, { params: { session_id: sessionId } });
      const pid = r.data.active_pack_id;
      if (!pid) { setInfo(null); return; }
      const p = (r.data.packs || []).find(x => x.id === pid);
      setInfo(p ? { id: p.id, name: p.name, count: p.count, updated_at: p.updated_at || p.created_at } : null);
    } catch { setInfo(null); }
  }, [sessionId]);

  useEffect(() => {
    fetch();
    const t = setInterval(fetch, 4000);
    const onEvt = () => fetch();
    window.addEventListener('wabot-active-pack-changed', onEvt);
    return () => { clearInterval(t); window.removeEventListener('wabot-active-pack-changed', onEvt); };
  }, [fetch]);

  if (!info) {
    return (
      <div className="flex items-center gap-2 text-xs text-stone-400 bg-stone-50 border border-dashed border-stone-300 rounded-xl px-4 py-2 mb-3" data-testid="wabot-active-pack-header-empty">
        <Package className="w-4 h-4" />
        לא נטענה קבוצה — שמור או טען קבוצה מהרשימה למטה
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 bg-gradient-to-l from-purple-600 to-purple-700 text-white rounded-xl px-4 py-3 mb-3 shadow-md" data-testid="wabot-active-pack-header">
      <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
        <Package className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-wide text-purple-200">קבוצת טריגרים פעילה</p>
        <p className="text-base font-black truncate" data-testid="wabot-active-pack-name">{info.name}</p>
      </div>
      <div className="text-xs text-purple-100 flex-shrink-0 text-left">
        <p className="font-bold">{info.count} טריגרים</p>
        <p className="opacity-75">{new Date(info.updated_at).toLocaleString('he-IL', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}</p>
      </div>
    </div>
  );
};

// ---------------- Trigger Card ----------------
const TriggerCard = ({ trigger, index, isSaving, onChange, onDelete }) => {
  return (
    <div
      className="bg-white rounded-2xl border-2 border-stone-200 p-5 relative hover:shadow-md transition-shadow"
      data-trigger-id={trigger.id}
      data-testid={`wabot-trigger-card-${trigger.id}`}
    >
      {/* Number Badge */}
      <div className="absolute -top-3 -right-3 w-8 h-8 bg-[#D97706] text-white rounded-full flex items-center justify-center font-black text-sm shadow-md">
        {index}
      </div>

      {/* Delete Button */}
      <button
        onClick={onDelete}
        className="absolute top-3 left-3 w-8 h-8 rounded-lg hover:bg-red-50 flex items-center justify-center text-stone-400 hover:text-red-500 transition-colors"
        data-testid={`wabot-trigger-delete-${trigger.id}`}
        title="מחק טריגר"
      >
        <X className="w-4 h-4" />
      </button>

      {/* Saving indicator */}
      {isSaving && (
        <div className="absolute top-3 left-12 text-xs text-stone-400 flex items-center gap-1">
          <Loader2 className="w-3 h-3 animate-spin" /> שומר
        </div>
      )}

      {/* ▒▒▒ STAGE 1 — Customer trigger keyword ▒▒▒ */}
      <SectionHeader colorClass="bg-green-100 text-green-700 border-green-200" stepNum="1" title="כשהלקוח כותב..." />
      <div className="mb-5 pl-1">
        <input
          value={trigger.keyword || ''}
          onChange={e => onChange('keyword', e.target.value)}
          placeholder="למשל: סוכר, סוכרת, יש לי סוכר   (כמה מילים מופרדות בפסיק)"
          className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 text-sm placeholder-stone-300 focus:outline-none focus:border-[#D97706] focus:bg-white transition-colors"
          data-testid={`wabot-trigger-keyword-${trigger.id}`}
        />
        <select
          value={trigger.match_type || 'contains'}
          onChange={e => onChange('match_type', e.target.value)}
          className="mt-2 text-xs bg-transparent text-stone-500 cursor-pointer focus:outline-none"
          data-testid={`wabot-trigger-match-${trigger.id}`}
        >
          <option value="contains">סוג התאמה: מכיל</option>
          <option value="exact">סוג התאמה: בדיוק</option>
          <option value="starts_with">סוג התאמה: מתחיל ב-</option>
        </select>
      </div>

      {/* divider between stage 1 and 2 */}
      <SectionDivider />

      {/* ▒▒▒ STAGE 2 — Bot reply ▒▒▒ */}
      <SectionHeader colorClass="bg-blue-100 text-blue-700 border-blue-200" stepNum="2" title="הבוט יענה..." />
      <div className="mb-5 pl-1">
        <textarea
          value={trigger.response || ''}
          onChange={e => onChange('response', e.target.value)}
          placeholder="הטקסט שהלקוח יקבל (השאר ריק = ללא תגובה)"
          rows={3}
          className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 text-sm placeholder-stone-300 focus:outline-none focus:border-[#D97706] focus:bg-white transition-colors resize-y"
          data-testid={`wabot-trigger-response-${trigger.id}`}
        />
        <TriggerMediaUpload trigger={trigger} onChange={onChange} />
      </div>

      <SectionDivider />

      {/* ▒▒▒ STAGE 3 — Smart follow-up ▒▒▒ */}
      <SectionHeader
        colorClass="bg-purple-100 text-purple-700 border-purple-200"
        stepNum="3"
        title="תגובה חוזרת חכמה"
        subtitle="אופציונלי - אחרי שהלקוח יענה לשאלה, הבוט ישלח את ההודעה הזו (להחלפה דינמית: {answer})"
      />
      <div className="mb-5 pl-1">
        <textarea
          value={trigger.followup_template || ''}
          onChange={e => onChange('followup_template', e.target.value)}
          placeholder="{answer}, תודה לך, אחזור אלייך בהקדם האפשרי. בנתיים אפשר להתרשם באתר: https://menshealth.co.il/products"
          rows={3}
          className="w-full bg-purple-50/30 border border-purple-200 rounded-xl px-4 py-2.5 text-sm placeholder-stone-300 focus:outline-none focus:border-purple-400 focus:bg-white transition-colors resize-y"
          data-testid={`wabot-trigger-followup-${trigger.id}`}
        />
      </div>

      <SectionDivider />

      {/* ▒▒▒ STAGE 4 — Scheduled reminders ▒▒▒ */}
      <ScheduledRepliesSection trigger={trigger} onChange={onChange} />

      {/* Stats */}
      {trigger.hit_count > 0 && (
        <div className="mt-3 pt-3 border-t border-stone-100 text-xs text-stone-500">
          הופעל {trigger.hit_count} פעמים
        </div>
      )}

      {/* Manual Save Button */}
      <SaveTriggerButton trigger={trigger} />
    </div>
  );
};

// Visible section header used between stages of a trigger card
const SectionHeader = ({ colorClass, stepNum, title, subtitle }) => (
  <div className="mb-3">
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border-2 font-black text-sm ${colorClass}`}>
      <span className="w-5 h-5 rounded-full bg-white inline-flex items-center justify-center text-xs">{stepNum}</span>
      {title}
    </div>
    {subtitle && <p className="text-xs text-stone-400 mt-1.5 mr-1">{subtitle}</p>}
  </div>
);

// Subtle horizontal divider between trigger card sections
const SectionDivider = () => (
  <div className="my-4 flex items-center gap-2" aria-hidden="true">
    <div className="flex-1 h-px bg-gradient-to-r from-transparent via-stone-300 to-transparent" />
    <ChevronDown className="w-3.5 h-3.5 text-stone-300" />
    <div className="flex-1 h-px bg-gradient-to-r from-transparent via-stone-300 to-transparent" />
  </div>
);

const SaveTriggerButton = ({ trigger }) => {
  const [state, setState] = useState('idle'); // idle | saving | saved
  const handleSave = async () => {
    setState('saving');
    try {
      await axios.put(`${API}/wabot/triggers/${trigger.id}`, {
        keyword: trigger.keyword || '',
        response: trigger.response || '',
        match_type: trigger.match_type || 'contains',
        enabled: trigger.enabled !== false,
        media_url: trigger.media_url || null,
        media_type: trigger.media_type || null,
        followup_template: trigger.followup_template || '',
        scheduled_replies: trigger.scheduled_replies || [],
      });
      setState('saved');
      toast.success('הטריגר נשמר!');
      setTimeout(() => setState('idle'), 2000);
    } catch {
      toast.error('שגיאה');
      setState('idle');
    }
  };
  return (
    <button
      onClick={handleSave}
      disabled={state === 'saving'}
      className={`w-full mt-4 rounded-xl py-2.5 font-bold text-sm flex items-center justify-center gap-2 transition-colors ${
        state === 'saved'
          ? 'bg-green-500 text-white'
          : 'bg-green-50 hover:bg-green-100 text-green-700 border-2 border-green-300'
      } disabled:opacity-50`}
      data-testid={`wabot-trigger-manual-save-${trigger.id}`}
    >
      {state === 'saving' ? <Loader2 className="w-4 h-4 animate-spin" /> :
       state === 'saved' ? <Check className="w-4 h-4" /> :
       <Save className="w-4 h-4" />}
      {state === 'saving' ? 'שומר...' : state === 'saved' ? 'נשמר!' : 'שמור טריגר'}
    </button>
  );
};

// ---------------- Scheduled Replies (timed reminders) ----------------
// Compute "היום HH:MM" / "מחר HH:MM" hint for a given target time string.
function computeTimeHint(timeStr) {
  if (!timeStr) return '';
  const m = /^(\d{1,2}):(\d{2})$/.exec(timeStr);
  if (!m) return '';
  const hh = parseInt(m[1], 10), mm = parseInt(m[2], 10);
  if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return '';
  const now = new Date();
  const target = new Date();
  target.setHours(hh, mm, 0, 0);
  return target <= now ? '(מחר)' : '(היום)';
}

const ScheduledRepliesSection = ({ trigger, onChange }) => {
  const items = trigger.scheduled_replies || [];

  const updateItem = (idx, patch) => {
    const next = items.map((it, i) => i === idx ? { ...it, ...patch } : it);
    onChange('scheduled_replies', next);
  };

  const addItem = () => {
    const next = [...items, { mode: 'delay', delay_minutes: 15, text: '' }];
    onChange('scheduled_replies', next);
  };

  const duplicateItem = (idx) => {
    const item = items[idx];
    const next = [...items.slice(0, idx + 1), { ...item }, ...items.slice(idx + 1)];
    onChange('scheduled_replies', next);
  };

  const removeItem = (idx) => {
    const next = items.filter((_, i) => i !== idx);
    onChange('scheduled_replies', next);
  };

  return (
    <div data-testid={`wabot-trigger-scheduled-${trigger.id}`}>
      <SectionHeader
        colorClass="bg-orange-100 text-orange-700 border-orange-200"
        stepNum="4"
        title="תזכורות מתוזמנות"
        subtitle="הודעות שיישלחו אוטומטית - אחרי X דקות מההפעלה, או בשעה ספציפית (היום/מחר). אם תכתוב ידנית ללקוח - כל התזכורות מתבטלות."
      />
      <div className="pl-1">
      {items.length > 0 && (
        <div className="space-y-2 mb-2">
          {items.map((item, idx) => {
            const mode = item.mode || (item.time ? 'time' : 'delay');
            return (
            <div key={idx} className="bg-orange-50 border border-orange-200 rounded-xl p-3" data-testid={`wabot-scheduled-row-${trigger.id}-${idx}`}>
              {/* Mode toggle (delay vs specific time) */}
              <div className="flex items-center gap-1 bg-white border border-orange-300 rounded-lg p-1 mb-2 w-fit" data-testid={`wabot-scheduled-mode-${trigger.id}-${idx}`}>
                <button
                  onClick={() => updateItem(idx, { mode: 'delay' })}
                  className={`px-3 py-1 rounded text-xs font-bold transition-colors ${mode === 'delay' ? 'bg-orange-500 text-white' : 'text-stone-500 hover:bg-orange-50'}`}
                  data-testid={`wabot-scheduled-mode-delay-${trigger.id}-${idx}`}
                >
                  אחרי X דקות
                </button>
                <button
                  onClick={() => updateItem(idx, { mode: 'time', time: item.time || '09:00' })}
                  className={`px-3 py-1 rounded text-xs font-bold transition-colors ${mode === 'time' ? 'bg-orange-500 text-white' : 'text-stone-500 hover:bg-orange-50'}`}
                  data-testid={`wabot-scheduled-mode-time-${trigger.id}-${idx}`}
                >
                  בשעה ספציפית
                </button>
              </div>

              <div className="flex items-center gap-2 mb-2 flex-wrap">
                {mode === 'delay' ? (
                  <div className="flex items-center gap-1 bg-white border border-orange-300 rounded-lg px-2 py-1.5 flex-shrink-0">
                    <span className="text-xs text-stone-500">אחרי</span>
                    <input
                      type="number"
                      min="1"
                      max="10080"
                      value={item.delay_minutes || 15}
                      onChange={e => updateItem(idx, { delay_minutes: Math.max(1, parseInt(e.target.value) || 15) })}
                      className="w-16 text-center text-sm font-bold bg-transparent focus:outline-none"
                      data-testid={`wabot-scheduled-minutes-${trigger.id}-${idx}`}
                    />
                    <span className="text-xs text-stone-500">דקות</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 bg-white border border-orange-300 rounded-lg px-2 py-1.5 flex-shrink-0">
                    <span className="text-xs text-stone-500">בשעה</span>
                    <input
                      type="time"
                      value={item.time || '09:00'}
                      onChange={e => updateItem(idx, { time: e.target.value })}
                      className="text-sm font-bold bg-transparent focus:outline-none"
                      data-testid={`wabot-scheduled-time-${trigger.id}-${idx}`}
                    />
                    <span className="text-[10px] text-stone-400 mr-1">{computeTimeHint(item.time || '09:00')}</span>
                  </div>
                )}
                <span className="text-xs text-orange-600 font-bold">→ שלח:</span>
                <div className="flex-1" />
                <button
                  onClick={() => duplicateItem(idx)}
                  className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                  title="שכפל את הזמן הזה"
                  data-testid={`wabot-scheduled-duplicate-${trigger.id}-${idx}`}
                >
                  <Copy className="w-4 h-4" />
                </button>
                <button
                  onClick={() => removeItem(idx)}
                  className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                  title="מחק"
                  data-testid={`wabot-scheduled-delete-${trigger.id}-${idx}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <textarea
                value={item.text || ''}
                onChange={e => updateItem(idx, { text: e.target.value })}
                placeholder="ההודעה שתישלח אוטומטית..."
                rows={2}
                className="w-full bg-white border border-orange-200 rounded-lg px-3 py-2 text-sm placeholder-stone-300 focus:outline-none focus:border-orange-400 resize-y"
                data-testid={`wabot-scheduled-text-${trigger.id}-${idx}`}
              />
            </div>
            );
          })}
        </div>
      )}

      <button
        onClick={addItem}
        className="w-full bg-orange-50 hover:bg-orange-100 border-2 border-dashed border-orange-300 text-orange-700 rounded-xl py-2.5 font-bold text-sm flex items-center justify-center gap-2 transition-colors"
        data-testid={`wabot-scheduled-add-${trigger.id}`}
      >
        <Plus className="w-4 h-4" /> הוסף תזכורת מתוזמנת
      </button>
      </div>
    </div>
  );
};

export default WaBotTriggers;

// ---------------- Trigger Packs Section ----------------
const TriggerPacksSection = ({ sessionId, onApply }) => {
  const [packs, setPacks] = useState([]);
  const [activePackId, setActivePackId] = useState(null);
  const [showSaveAs, setShowSaveAs] = useState(false);
  const [packName, setPackName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchPacks = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/wabot/trigger-packs`, {
        params: sessionId ? { session_id: sessionId } : {},
      });
      setPacks(r.data.packs || []);
      setActivePackId(r.data.active_pack_id || null);
      window.dispatchEvent(new Event('wabot-active-pack-changed'));
    } catch {}
  }, [sessionId]);

  useEffect(() => { fetchPacks(); }, [fetchPacks]);

  const activePack = packs.find(p => p.id === activePackId);

  // Save current triggers INTO the active pack (no prompt). Used by the main "save" button.
  const saveActive = async () => {
    if (!sessionId) { toast.error('בחר מכשיר תחילה'); return; }
    if (!activePackId) { setShowSaveAs(true); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/wabot/trigger-packs/${activePackId}/snapshot`, { session_id: sessionId });
      toast.success(`נשמרו ${r.data.count} טריגרים בקבוצה "${activePack?.name}"`);
      fetchPacks();
    } catch { toast.error('שגיאה בשמירה'); }
    setSaving(false);
  };

  // Save AS a new named pack
  const saveAsNew = async () => {
    if (!packName.trim()) { toast.error('שם נדרש'); return; }
    if (!sessionId) { toast.error('בחר מכשיר'); return; }
    setSaving(true);
    try {
      const existing = packs.find(p => p.name.trim() === packName.trim());
      if (existing) {
        if (!window.confirm(`קיימת קבוצה בשם "${packName}". להחליף את התוכן שלה בטריגרים הנוכחיים?`)) {
          setSaving(false); return;
        }
        await axios.post(`${API}/wabot/trigger-packs/${existing.id}/snapshot`, { session_id: sessionId });
        toast.success('הקבוצה עודכנה!');
      } else {
        await axios.post(`${API}/wabot/trigger-packs`, { name: packName.trim(), session_id: sessionId });
        toast.success('קבוצה חדשה נשמרה!');
      }
      setPackName(''); setShowSaveAs(false);
      fetchPacks();
    } catch { toast.error('שגיאה'); }
    setSaving(false);
  };

  // Open a pack: replace current triggers with its content. ALL fields preserved (followup/scheduled/enabled/media)
  const openPack = async (p) => {
    if (!sessionId) { toast.error('בחר מכשיר תחילה'); return; }
    if (p.id === activePackId) { toast.info(`כבר נמצאים בקבוצה "${p.name}"`); return; }
    if (!window.confirm(`לטעון את הקבוצה "${p.name}"?\nהטריגרים הנוכחיים יוחלפו ב-${p.count} מהקבוצה.`)) return;
    try {
      const r = await axios.post(`${API}/wabot/trigger-packs/${p.id}/apply`, { session_id: sessionId, replace: true });
      toast.success(`נטענה הקבוצה "${p.name}" - ${r.data.added} טריגרים`);
      setActivePackId(p.id);
      onApply?.();
      fetchPacks();
    } catch (e) {
      const msg = e.response?.data?.detail || 'שגיאה בטעינה';
      toast.error(msg);
    }
  };

  const renamePack = async (p) => {
    if (!editName.trim()) { setEditingId(null); return; }
    try {
      await axios.put(`${API}/wabot/trigger-packs/${p.id}`, { name: editName.trim() });
      setEditingId(null);
      fetchPacks();
      toast.success('שם עודכן');
    } catch { toast.error('שגיאה'); }
  };

  const deletePack = async (p) => {
    if (!window.confirm(`למחוק את "${p.name}"?`)) return;
    await axios.delete(`${API}/wabot/trigger-packs/${p.id}`);
    fetchPacks();
    toast.success('נמחק');
  };

  // Append pack triggers to current (without replacing)
  const appendPack = async (p, e) => {
    e.stopPropagation();
    if (!window.confirm(`להוסיף ${p.count} טריגרים מ"${p.name}" לטריגרים הנוכחיים?\n(הקיימים יישארו)`)) return;
    try {
      await axios.post(`${API}/wabot/trigger-packs/${p.id}/apply`, { session_id: sessionId, replace: false });
      toast.success(`נוספו ${p.count} טריגרים`);
      onApply?.();
      fetchPacks();
    } catch { toast.error('שגיאה'); }
  };

  const exitActivePack = async () => {
    if (!window.confirm('לצאת מהקבוצה הפעילה?\n(הטריגרים יישארו - רק לא יהיו משויכים לקבוצה)')) return;
    try {
      await axios.post(`${API}/wabot/trigger-packs/clear-active`, { session_id: sessionId });
      setActivePackId(null);
      fetchPacks();
    } catch { toast.error('שגיאה'); }
  };

  return (
    <div className="bg-white rounded-2xl border-2 border-stone-200 p-5 mt-6" data-testid="wabot-trigger-packs">
      {/* Active pack header banner */}
      {activePack ? (
        <div className="bg-gradient-to-l from-purple-100 to-purple-50 border-2 border-purple-400 rounded-xl p-4 mb-4 flex items-center gap-3" data-testid="wabot-active-pack-banner">
          <div className="w-12 h-12 rounded-xl bg-purple-600 flex items-center justify-center flex-shrink-0 shadow-md">
            <Package className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-purple-600 font-bold uppercase tracking-wide">קבוצה פעילה כעת</p>
            <p className="text-lg font-black text-purple-900 truncate">{activePack.name}</p>
            <p className="text-xs text-purple-700">{activePack.count} טריגרים · עודכן {new Date(activePack.updated_at || activePack.created_at).toLocaleString('he-IL')}</p>
          </div>
          <div className="flex flex-col gap-1.5 flex-shrink-0">
            <button onClick={saveActive} disabled={saving} className="bg-purple-600 hover:bg-purple-700 text-white rounded-lg px-3 py-2 text-xs font-bold flex items-center gap-1 disabled:opacity-50" data-testid="wabot-active-pack-save">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Save className="w-3.5 h-3.5" /> שמור שינויים</>}
            </button>
            <button onClick={exitActivePack} className="bg-white hover:bg-stone-50 border border-purple-300 text-purple-700 rounded-lg px-3 py-1.5 text-xs font-bold" data-testid="wabot-active-pack-exit">
              צא מהקבוצה
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-stone-50 border border-dashed border-stone-300 rounded-xl p-3 mb-4 text-center" data-testid="wabot-no-active-pack">
          <p className="text-xs text-stone-500">לא נטענה קבוצה כעת. הטריגרים שמטה הם הטריגרים הפעילים על המכשיר.</p>
        </div>
      )}

      <div className="flex items-center justify-between mb-3">
        <h4 className="font-black text-stone-700 flex items-center gap-2">
          <Package className="w-5 h-5 text-purple-500" />
          קבוצות שמורות ({packs.length})
        </h4>
        <button
          onClick={() => setShowSaveAs(!showSaveAs)}
          className="bg-purple-50 border-2 border-purple-300 text-purple-700 hover:bg-purple-100 rounded-lg px-3 py-1.5 text-sm font-bold flex items-center gap-1"
          data-testid="wabot-pack-save-as-btn"
        >
          <Plus className="w-4 h-4" /> שמור כקבוצה חדשה
        </button>
      </div>

      {showSaveAs && (
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 mb-3" data-testid="wabot-pack-save-form">
          <p className="text-xs text-purple-700 mb-2">יוצר תצלום של כל הטריגרים הנוכחיים כקבוצה חדשה.</p>
          <div className="flex gap-2">
            <input
              value={packName}
              onChange={e => setPackName(e.target.value)}
              placeholder="שם הקבוצה (למשל: בוט מכירות)"
              className="flex-1 border border-purple-300 rounded-lg px-3 py-2 text-sm bg-white"
              onKeyDown={e => e.key === 'Enter' && saveAsNew()}
              data-testid="wabot-pack-name-input"
              autoFocus
            />
            <button onClick={saveAsNew} disabled={saving} className="bg-purple-600 hover:bg-purple-700 text-white rounded-lg px-4 py-2 text-sm font-bold disabled:opacity-50" data-testid="wabot-pack-save-confirm">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'שמור'}
            </button>
            <button onClick={() => { setShowSaveAs(false); setPackName(''); }} className="bg-stone-100 hover:bg-stone-200 rounded-lg px-3 py-2 text-sm">ביטול</button>
          </div>
        </div>
      )}

      {packs.length === 0 ? (
        <p className="text-sm text-stone-400 text-center py-3">אין קבוצות שמורות. שמור קבוצה כדי להעביר טריגרים מהר בין מכשירים.</p>
      ) : (
        <div className="space-y-2">
          {packs.map(p => {
            const isActive = p.id === activePackId;
            return (
              <div
                key={p.id}
                className={`rounded-lg p-3 flex items-center gap-3 transition-all cursor-pointer border-2 ${isActive ? 'bg-purple-50 border-purple-500 shadow-sm' : 'bg-stone-50 hover:bg-purple-50 border-stone-200'}`}
                data-testid={`wabot-pack-${p.id}`}
                data-active={isActive ? 'true' : 'false'}
                onClick={() => editingId !== p.id && openPack(p)}
                role="button"
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isActive ? 'bg-purple-600' : 'bg-purple-100'}`}>
                  <Package className={`w-5 h-5 ${isActive ? 'text-white' : 'text-purple-600'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  {editingId === p.id ? (
                    <input value={editName} onChange={e => setEditName(e.target.value)} onClick={e => e.stopPropagation()} onKeyDown={e => e.key === 'Enter' && renamePack(p)} autoFocus className="w-full border rounded px-2 py-1 text-sm font-bold" data-testid={`wabot-pack-edit-input-${p.id}`} />
                  ) : (
                    <div className="flex items-center gap-2">
                      <p className={`font-bold truncate ${isActive ? 'text-purple-900' : 'text-[#2C3E2D]'}`}>{p.name}</p>
                      {isActive && <span className="bg-purple-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0">פעיל כעת</span>}
                    </div>
                  )}
                  <p className="text-xs text-stone-500">{p.count} טריגרים · {new Date(p.updated_at || p.created_at).toLocaleDateString('he-IL')}</p>
                </div>
                <div className="flex gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                  {editingId === p.id ? (
                    <>
                      <button onClick={() => renamePack(p)} className="p-1.5 text-green-600 hover:bg-green-50 rounded"><Check className="w-4 h-4" /></button>
                      <button onClick={() => setEditingId(null)} className="p-1.5 text-stone-400 hover:bg-stone-100 rounded"><X className="w-4 h-4" /></button>
                    </>
                  ) : (
                    <>
                      <button onClick={(e) => appendPack(p, e)} className="px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white rounded font-bold text-xs flex items-center gap-1" title="הוסף לטריגרים הקיימים בלי להחליף" data-testid={`wabot-pack-apply-${p.id}`}>
                        <Plus className="w-3.5 h-3.5" /> הוסף
                      </button>
                      <button onClick={() => { setEditingId(p.id); setEditName(p.name); }} className="p-1.5 text-amber-600 hover:bg-amber-50 rounded" title="ערוך שם" data-testid={`wabot-pack-edit-${p.id}`}><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => deletePack(p)} className="p-1.5 text-red-500 hover:bg-red-50 rounded" title="מחק" data-testid={`wabot-pack-delete-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ---------------- Media Upload ----------------
const TriggerMediaUpload = ({ trigger, onChange }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error('קובץ גדול מדי (מקסימום 25MB)');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/wabot/upload-media`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange('media_url', r.data.url);
      onChange('media_type', r.data.type);
      toast.success('מדיה הועלתה');
    } catch (err) {
      toast.error('שגיאה בהעלאה');
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const removeMedia = () => {
    onChange('media_url', null);
    onChange('media_type', null);
  };

  if (trigger.media_url) {
    const fullUrl = trigger.media_url.startsWith('/') ? `${BACKEND_ORIGIN}${trigger.media_url}` : trigger.media_url;
    return (
      <div className="mt-3 p-3 bg-blue-50 border-2 border-blue-200 rounded-xl flex items-center gap-3" data-testid={`wabot-trigger-media-preview-${trigger.id}`}>
        {trigger.media_type === 'video' ? (
          <video src={fullUrl} className="w-20 h-20 object-cover rounded-lg" controls={false} />
        ) : (
          <img src={fullUrl} alt="media" className="w-20 h-20 object-cover rounded-lg" />
        )}
        <div className="flex-1 text-sm">
          <p className="font-bold text-blue-900 flex items-center gap-1">
            {trigger.media_type === 'video' ? <Film className="w-3.5 h-3.5" /> : <ImageIcon className="w-3.5 h-3.5" />}
            {trigger.media_type === 'video' ? 'סרטון' : 'תמונה'} מצורף
          </p>
          <p className="text-xs text-blue-700 truncate">{trigger.media_url}</p>
        </div>
        <button onClick={removeMedia} className="text-red-500 hover:bg-red-50 p-2 rounded-lg" data-testid={`wabot-trigger-media-remove-${trigger.id}`}>
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*,video/*"
        onChange={handleFile}
        className="hidden"
        data-testid={`wabot-trigger-media-input-${trigger.id}`}
      />
      <button
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="mt-3 w-full bg-blue-50 border-2 border-dashed border-blue-300 text-blue-600 hover:bg-blue-100 rounded-xl py-3 text-sm font-bold flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
        data-testid={`wabot-trigger-attach-${trigger.id}`}
      >
        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
        צרף תמונה / סרטון לתגובה (אופציונלי)
      </button>
    </>
  );
};

// ---------------- AI Assistant Card ----------------
const AiAssistantCard = ({ sessionId, expanded, onToggle }) => {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const saveTimer = useRef(null);

  useEffect(() => {
    if (!sessionId) return;
    axios.get(`${API}/wabot/ai-config/${sessionId}`).then(r => setConfig(r.data)).catch(() => {});
  }, [sessionId]);

  const updateConfig = (patch) => {
    const next = { ...config, ...patch };
    setConfig(next);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        await axios.put(`${API}/wabot/ai-config/${sessionId}`, {
          enabled: next.enabled, model: next.model, system_prompt: next.system_prompt,
        });
      } catch { toast.error('שגיאה'); }
      setSaving(false);
    }, 700);
  };

  if (!config) return null;

  return (
    <div className={`bg-gradient-to-l from-purple-50 to-violet-100 rounded-2xl border-2 mb-4 overflow-hidden ${config.enabled ? 'border-purple-400 shadow-md' : 'border-purple-200'}`} data-testid="wabot-ai-card">
      <div className="w-full p-5 flex items-center gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${config.enabled ? 'bg-purple-600' : 'bg-purple-400'}`}>
          <Sparkles className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h3 className="font-black text-purple-900 flex items-center gap-2">
            עוזר AI חכם (Gemini)
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${config.enabled ? 'bg-green-500 text-white' : 'bg-stone-200 text-stone-600'}`}>
              {config.enabled ? 'פעיל' : 'כבוי'}
            </span>
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-purple-500" />}
          </h3>
          <p className="text-xs text-purple-700 mt-1">מאפשר לבוט לענות בצורה אנושית וספונטנית מבוסס על המידע באתר שלך</p>
        </div>
        {/* Toggle Switch */}
        <label className="relative inline-flex items-center cursor-pointer" data-testid="wabot-ai-toggle">
          <input type="checkbox" checked={config.enabled} onChange={e => updateConfig({ enabled: e.target.checked })} className="sr-only peer" />
          <div className="w-12 h-6 bg-stone-300 rounded-full peer peer-checked:bg-purple-600 transition-colors after:content-[''] after:absolute after:top-0.5 after:right-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:-translate-x-6"></div>
        </label>
        <button onClick={onToggle} className="p-1 hover:bg-purple-100 rounded">
          {expanded ? <ChevronUp className="w-5 h-5 text-purple-500" /> : <ChevronDown className="w-5 h-5 text-purple-500" />}
        </button>
      </div>
      {expanded && (
        <div className="px-5 pb-5 border-t border-purple-200 pt-4 bg-white/50 space-y-4">
          <div>
            <label className="block text-xs font-bold text-purple-900 mb-1">מודל AI</label>
            <select value={config.model} onChange={e => updateConfig({ model: e.target.value })} className="w-full border border-purple-200 rounded-lg px-3 py-2 text-sm bg-white" data-testid="wabot-ai-model">
              <option value="gemini-2.5-flash">Gemini 2.5 Flash (מהיר וזול)</option>
              <option value="gemini-2.5-pro">Gemini 2.5 Pro (חכם יותר)</option>
              <option value="gemini-3-flash-preview">Gemini 3 Flash Preview</option>
              <option value="gemini-3.1-pro-preview">Gemini 3.1 Pro Preview</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-purple-900 mb-1">הוראות מערכת (System Prompt)</label>
            <textarea
              value={config.system_prompt || ''}
              onChange={e => updateConfig({ system_prompt: e.target.value })}
              rows={5}
              placeholder="אתה נציג שירות לקוחות של בריאות הגבר..."
              className="w-full border border-purple-200 rounded-lg px-3 py-2 text-sm bg-white"
              data-testid="wabot-ai-prompt"
            />
            <p className="text-[10px] text-purple-600 mt-1">הסבר לבוט מי הוא, איך לענות, ומה החנות מציעה. זה ייתן לו אישיות עקבית.</p>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-xs text-purple-800">
            <p className="font-bold mb-1">📚 איך זה עובד?</p>
            <p>כשלקוח כותב הודעה <b>שלא תפסה אף טריגר רגיל</b>, ה-AI יענה אוטומטית. הוא זוכר את ההיסטוריה של השיחה (פר מספר טלפון).</p>
          </div>
        </div>
      )}
    </div>
  );
};
